import { useEffect, useRef, useState } from "react";
import { Type, Highlighter, Plus, Check } from "lucide-react";

/**
 * Highlight + text-color popovers used by the doc editor.
 *
 * - Each shows a 6×N grid of preset swatches plus a custom color picker
 *   that, on commit, gets stored to localStorage so the user can re-use it.
 * - The highlight handler also auto-flips the foreground colour (white on
 *   dark backgrounds, black on light) so the text stays readable. This is
 *   skipped if the user has explicitly set a text colour (tracked through
 *   `userPickedTextColorRef` in the parent component).
 *
 * User request:
 *   "Highlighting should be a dropdown of colors with one option to add a
 *    custom color that gets stored for reuse. Highlighting with a selected
 *    color should change the text color to dark/light if the highlight
 *    makes the text unreadable. This will only be overwritten if the text
 *    color is defined as custom. Add text color and highlight to the navbar."
 */

const HIGHLIGHT_PRESETS = [
  "transparent", "#FFEB3B", "#FFC107", "#FF9800", "#FF5722", "#F44336",
  "#E91E63", "#9C27B0", "#673AB7", "#3F51B5", "#2196F3", "#03A9F4",
  "#00BCD4", "#009688", "#4CAF50", "#8BC34A", "#CDDC39", "#FFFFFF",
];

const TEXT_PRESETS = [
  "#EDEDED", "#FFFFFF", "#A1A1AA", "#0A0A0A", "#FF3B30", "#FF8A00",
  "#FFEB3B", "#34C759", "#00BCD4", "#5A90F5", "#A855F7", "#FF2D92",
];

const STORE_KEY = "cresc-doc-recent-colors";

function loadRecent() {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); }
  catch { return {}; }
}

function saveRecent(kind, color) {
  const all = loadRecent();
  const arr = all[kind] || [];
  const next = [color, ...arr.filter((c) => c.toLowerCase() !== color.toLowerCase())].slice(0, 6);
  all[kind] = next;
  try { localStorage.setItem(STORE_KEY, JSON.stringify(all)); } catch {}
  return next;
}

/** Perceived-brightness contrast helper. Returns "#0A0A0A" (near-black)
 *  for highlights bright enough that black text reads cleanly, "#FFFFFF"
 *  only for genuinely dark backgrounds where white is required.
 *
 *  Uses the YIQ formula tuned so MOST highlight colors produce black text
 *  (user feedback: "with red color, white text is too bright and hard to
 *  read; most of the time, the text should be black when highlight, but
 *  white when low contrast"). Threshold of 110 over 255 places red,
 *  orange, yellow, green, cyan, light blue, white → black; only deep
 *  navy / purple / dark grey → white. */
export function contrastFor(hex) {
  if (!hex || hex === "transparent" || hex === "rgba(0, 0, 0, 0)") return "#EDEDED";
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length < 6) return "#EDEDED";
  const r = parseInt(h.substr(0, 2), 16);
  const g = parseInt(h.substr(2, 2), 16);
  const b = parseInt(h.substr(4, 2), 16);
  // YIQ perceived brightness (0..255).
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 110 ? "#0A0A0A" : "#FFFFFF";
}

function ColorPopover({ kind, presets, onPick, anchorTitle, "data-testid": testId, icon: Icon, isHighlight = false }) {
  const [open, setOpen] = useState(false);
  const [recents, setRecents] = useState(() => loadRecent()[kind] || []);
  // Live custom-color preview state — drives the "Aa" sample chip below
  // the custom input so the user sees readability while dragging the
  // color wheel BEFORE they commit (user-suggested improvement: "make
  // the highlight smarter — auto-suggest a complementary text color in
  // real-time so the user can see readability before committing").
  const [previewColor, setPreviewColor] = useState("#5A90F5");
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);
  const pick = (c) => {
    onPick(c);
    setRecents(saveRecent(kind, c));
    setOpen(false);
  };
  return (
    <div ref={wrap} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={anchorTitle}
        className="inline-flex items-center justify-center w-7 h-7 rounded border border-[#2A2A2A] bg-[#0A0A0A] hover:border-[#5A90F5]"
        data-testid={testId}
      >
        <Icon className="w-3.5 h-3.5 text-[#EDEDED]" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-[60] bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl p-2 w-[200px]"
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testId}-popover`}
        >
          <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-1.5">Presets</div>
          <div className="grid grid-cols-6 gap-1 mb-2">
            {presets.map((c) => (
              <button
                key={c}
                onClick={() => pick(c)}
                title={c === "transparent" ? "No highlight" : c}
                style={{ background: c === "transparent" ? "#0A0A0A" : c }}
                className={`w-7 h-7 rounded border ${c === "transparent" ? "border-dashed border-[#A1A1AA]" : "border-[#2A2A2A]"} hover:ring-2 hover:ring-[#5A90F5] flex items-center justify-center`}
                data-testid={`${testId}-preset-${c.replace(/[#]/g, "")}`}
              >
                {c === "transparent" && <span className="text-[10px] text-[#A1A1AA]">×</span>}
              </button>
            ))}
          </div>
          {recents.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-1.5">Recent</div>
              <div className="grid grid-cols-6 gap-1 mb-2">
                {recents.map((c) => (
                  <button
                    key={c}
                    onClick={() => pick(c)}
                    title={c}
                    style={{ background: c }}
                    className="w-7 h-7 rounded border border-[#2A2A2A] hover:ring-2 hover:ring-[#5A90F5]"
                    data-testid={`${testId}-recent`}
                  />
                ))}
              </div>
            </>
          )}
          <label className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-[#3A3A3A] bg-[#0A0A0A] cursor-pointer text-[11px] text-[#EDEDED] hover:border-[#5A90F5]" data-testid={`${testId}-custom`}>
            <Plus className="w-3 h-3" /> Custom
            <input
              type="color"
              defaultValue="#5A90F5"
              onInput={(e) => setPreviewColor(e.target.value)}
              onChange={(e) => pick(e.target.value)}
              className="ml-auto cursor-pointer rounded bg-[#0A0A0A] border-0"
              style={{ width: 24, height: 20 }}
            />
          </label>
          {/* Live preview chip — only meaningful for the highlight control
              (text-color popover doesn't need contrast preview). Shows
              "Aa" rendered in the auto-computed text color over the
              chosen highlight, so the user sees readability before
              committing. */}
          {isHighlight && (
            <div
              className="mt-2 rounded border border-[#2A2A2A] px-2 py-1.5 text-center text-[12px] font-display"
              style={{ background: previewColor, color: contrastFor(previewColor) }}
              data-testid={`${testId}-preview`}
              title={`Preview at ${previewColor} → text ${contrastFor(previewColor)}`}
            >
              Aa <span className="opacity-70 text-[10px]">{previewColor.toUpperCase()}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Public components consumed by the editor toolbar + ctx menu. */
export function HighlightControl({ exec, userPickedTextColorRef, setLastForeColor, "data-testid": tid = "docs-highlight" }) {
  return (
    <ColorPopover
      kind="hl"
      icon={Highlighter}
      anchorTitle="Highlight"
      presets={HIGHLIGHT_PRESETS}
      data-testid={tid}
      isHighlight
      onPick={(c) => {
        document.execCommand("styleWithCSS", false, true);
        if (c === "transparent") {
          exec("hiliteColor", "transparent");
          // Restore foreground when highlight is cleared (unless the user
          // explicitly chose a text color).
          if (!userPickedTextColorRef?.current) exec("foreColor", "#EDEDED");
          return;
        }
        exec("hiliteColor", c);
        if (!userPickedTextColorRef?.current) {
          const fg = contrastFor(c);
          exec("foreColor", fg);
          if (setLastForeColor) setLastForeColor(fg);
        }
      }}
    />
  );
}

export function TextColorControl({ exec, userPickedTextColorRef, "data-testid": tid = "docs-textcolor" }) {
  return (
    <ColorPopover
      kind="text"
      icon={Type}
      anchorTitle="Text color"
      presets={TEXT_PRESETS}
      data-testid={tid}
      onPick={(c) => {
        document.execCommand("styleWithCSS", false, true);
        exec("foreColor", c);
        if (userPickedTextColorRef) userPickedTextColorRef.current = true;
      }}
    />
  );
}
